// client/src/contexts/BenchmarkContext.tsx
//
// Contexto React para benchmarks configurables por team.
// Por defecto delega a los *Benchmarks.ts hardcodeados (cero breaking change).
// Cuando hay overrides del team, los aplica primero con fallback a defaults.
//
// Fallback de stack: si no hay override para el stack específico (ej: 'short')
// pero sí para 'all', el override de 'all' aplica. Esto permite al coach setear
// un override global en el tab ALL sin tener que repetirlo en SHORT/MID/DEEP.

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { Position, OpenPosition } from '../utils/pokerStats/types';
import { CbetTexturePosKey, FlopTexture, ShoverStackBin } from '../utils/pokerStats/types';
import {
  LeakRange, LeakStatus, StackKey,
  getRange as defaultGetRange,
} from '../components/stats/leakRanges';
import {
  VpipBenchmark, VpipStatus,
  getVpipBenchmark as defaultGetVpipBenchmark,
} from '../components/stats/vpipBenchmarks';
import {
  RfiBenchmark, RfiStatus,
  getRfiBenchmark as defaultGetRfiBenchmark,
} from '../components/stats/rfiBenchmarks';
import {
  getCbetTextureBenchmark as defaultGetCbetTextureBenchmark,
} from '../components/stats/cbetTextureBenchmarks';
import {
  getCbetTextureMultiBenchmark as defaultGetCbetTextureMultiBenchmark,
} from '../components/stats/cbetTextureMultiBenchmarks';
import {
  getPushFoldBenchmark as defaultGetPushFoldBenchmark,
} from '../components/stats/nashPushFoldBenchmarks';
import {
  FoldToPushBenchmark, FoldToPushStatus,
  getFoldToPushBenchmark as defaultGetFoldToPushBenchmark,
} from '../components/stats/foldToPushBenchmarks';
import { Benchmark } from '../components/stats/benchmarks';

// ── Tipos de overrides ────────────────────────────────────────────────────────

export interface BenchmarkOverride {
  min: number;
  max: number;
}

export interface TeamBenchmarkOverrides {
  leakRanges?:       Partial<Record<StackKey, Partial<Record<string, BenchmarkOverride>>>>;
  vpip?:             Partial<Record<string, BenchmarkOverride>>;
  rfi?:              Partial<Record<StackKey, Partial<Record<string, BenchmarkOverride>>>>;
  cbetTexture?:      Partial<Record<string, BenchmarkOverride>>;
  cbetTextureMulti?: Partial<Record<string, BenchmarkOverride>>;
  pushFold?:         Partial<Record<string, BenchmarkOverride>>;
  foldToPush?:       Partial<Record<string, BenchmarkOverride>>;
}

// ── Interfaz del contexto ─────────────────────────────────────────────────────

export interface BenchmarkContextValue {
  evalStat:                  (key: string, value: number, stackSize: StackKey) => LeakStatus;
  getRange:                  (key: string, stackSize: StackKey) => LeakRange | null;
  evalVpip:                  (position: Position, value: number) => VpipStatus;
  getVpipBenchmark:          (position: Position) => VpipBenchmark | null;
  evalRfi:                   (position: OpenPosition, value: number, stack: StackKey) => RfiStatus;
  getRfiBenchmark:           (position: OpenPosition, stack: StackKey) => RfiBenchmark | null;
  getCbetTextureBenchmark:   (key: string) => Benchmark | null;
  getCbetTextureMultiBenchmark: (texture: string) => Benchmark | null;
  getPushFoldBenchmark:      (pos: string) => Benchmark | null;
  getFoldToPushBenchmark:    (bin: string) => FoldToPushBenchmark | null;
  evalFoldToPush:            (bin: string, value: number) => FoldToPushStatus;
}

// ── Helpers de eval (lógica reutilizable) ─────────────────────────────────────

function evalRange(value: number, r: { min: number; max: number }, fallbackGap = 5): 'ok' | 'warn' | 'critical' {
  if (value >= r.min && value <= r.max) return 'ok';
  const gap   = value > r.max ? value - r.max : r.min - value;
  const width = r.max - r.min || fallbackGap;
  return gap > width * 1.5 ? 'critical' : 'warn';
}

// ── Construcción del valor del contexto ───────────────────────────────────────

function buildContextValue(overrides: TeamBenchmarkOverrides | null): BenchmarkContextValue {
  // Lee override con fallback de stack: primero stackSize, luego 'all' si diffstackSize !== 'all'
  function lrOverride(key: string, stackSize: StackKey): BenchmarkOverride | undefined {
    const direct = overrides?.leakRanges?.[stackSize]?.[key];
    if (direct) return direct;
    if (stackSize !== 'all') return overrides?.leakRanges?.['all']?.[key];
    return undefined;
  }
  function rfiOverride(pos: string, stackSize: StackKey): BenchmarkOverride | undefined {
    const direct = overrides?.rfi?.[stackSize]?.[pos];
    if (direct) return direct;
    if (stackSize !== 'all') return overrides?.rfi?.['all']?.[pos];
    return undefined;
  }

  const getRange_ = (key: string, stackSize: StackKey): LeakRange | null => {
    const ov = lrOverride(key, stackSize);
    if (ov) {
      const def = defaultGetRange(key, stackSize);
      return { ...ov, label: def?.label || key };
    }
    return defaultGetRange(key, stackSize);
  };

  const evalStat_ = (key: string, value: number, stackSize: StackKey): LeakStatus => {
    const r = getRange_(key, stackSize);
    if (!r) return 'ok';
    return evalRange(value, r);
  };

  const getVpipBenchmark_ = (position: Position): VpipBenchmark | null => {
    const ov = overrides?.vpip?.[position as string];
    if (ov) return ov;
    return defaultGetVpipBenchmark(position);
  };

  const evalVpip_ = (position: Position, value: number): VpipStatus => {
    const r = getVpipBenchmark_(position);
    if (!r) return 'ok';
    return evalRange(value, r);
  };

  const getRfiBenchmark_ = (position: OpenPosition, stack: StackKey): RfiBenchmark | null => {
    const ov = rfiOverride(position as string, stack);
    if (ov) return ov;
    return defaultGetRfiBenchmark(position, stack);
  };

  const evalRfi_ = (position: OpenPosition, value: number, stack: StackKey): RfiStatus => {
    const r = getRfiBenchmark_(position, stack);
    if (!r) return 'ok';
    return evalRange(value, r);
  };

  const getCbetTextureBenchmark_ = (key: string): Benchmark | null => {
    const ov = overrides?.cbetTexture?.[key];
    if (ov) return { ...ov, source: 'theoretical' as const };
    return defaultGetCbetTextureBenchmark(key as CbetTexturePosKey);
  };

  const getCbetTextureMultiBenchmark_ = (texture: string): Benchmark | null => {
    const ov = overrides?.cbetTextureMulti?.[texture];
    if (ov) return { ...ov, source: 'theoretical' as const };
    return defaultGetCbetTextureMultiBenchmark(texture as FlopTexture);
  };

  const getPushFoldBenchmark_ = (pos: string): Benchmark | null => {
    const ov = overrides?.pushFold?.[pos];
    if (ov) return { ...ov, source: 'theoretical' as const };
    return defaultGetPushFoldBenchmark(pos as OpenPosition);
  };

  const getFoldToPushBenchmark_ = (bin: string): FoldToPushBenchmark | null => {
    const ov = overrides?.foldToPush?.[bin];
    if (ov) return ov;
    return defaultGetFoldToPushBenchmark(bin as ShoverStackBin);
  };

  const evalFoldToPush_ = (bin: string, value: number): FoldToPushStatus => {
    const r = getFoldToPushBenchmark_(bin);
    if (!r) return 'ok';
    return evalRange(value, r, 10);
  };

  return {
    evalStat:                  evalStat_,
    getRange:                  getRange_,
    evalVpip:                  evalVpip_,
    getVpipBenchmark:          getVpipBenchmark_,
    evalRfi:                   evalRfi_,
    getRfiBenchmark:           getRfiBenchmark_,
    getCbetTextureBenchmark:   getCbetTextureBenchmark_,
    getCbetTextureMultiBenchmark: getCbetTextureMultiBenchmark_,
    getPushFoldBenchmark:      getPushFoldBenchmark_,
    getFoldToPushBenchmark:    getFoldToPushBenchmark_,
    evalFoldToPush:            evalFoldToPush_,
  };
}

// ── Contexto React ────────────────────────────────────────────────────────────

const defaultValue = buildContextValue(null);
const BenchmarkContext = createContext<BenchmarkContextValue>(defaultValue);

interface BenchmarkProviderProps {
  children:  ReactNode;
  overrides: TeamBenchmarkOverrides | null;
}

export function BenchmarkProvider({ children, overrides }: BenchmarkProviderProps) {
  const value = useMemo(() => buildContextValue(overrides), [overrides]);
  return (
    <BenchmarkContext.Provider value={value}>
      {children}
    </BenchmarkContext.Provider>
  );
}

export function useBenchmarks(): BenchmarkContextValue {
  return useContext(BenchmarkContext);
}
